/**
 * Admin list for real weddings or blogs (filtered by content_type).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRealWeddingReducer } from "../../Utility";
import {
  onGetRealWeddings,
  onRealWeddingPost,
  onRealWeddingPostReset,
} from "../../Store/Slices/realWeddingSlice";
import CommonTable from "../Components/CommonTable";
import "./RealWeddingsListPage.css";

function ContentListPage({
  contentType,
  title,
  addLabel,
  addPath,
  editPathPrefix,
  viewPathPrefix = "/real-weddings",
  emptyText,
  searchPlaceholder,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getListData, getListLoading, postLoading, postData } = useRealWeddingReducer();
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isBlog = contentType === "blog";

  useEffect(() => {
    dispatch(onGetRealWeddings());
  }, [dispatch]);

  useEffect(() => {
    if (!postData) return;
    dispatch(onGetRealWeddings());
    dispatch(onRealWeddingPostReset());
    setDeletingId(null);
    setDeleteTarget(null);
  }, [postData, dispatch]);

  const list = useMemo(() => {
    const items = Array.isArray(getListData) ? getListData : [];
    return items.filter((item) => {
      if (item?.is_deleted === true) return false;
      const type = item?.content_type === "blog" ? "blog" : "real_wedding";
      return type === contentType;
    });
  }, [getListData, contentType]);

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    const row = deleteTarget;
    const id = row?.id ?? row?.real_wedding_id;
    if (id == null || postLoading) return;
    setDeletingId(id);
    dispatch(
      onRealWeddingPost({
        id,
        content_type: contentType,
        heading: row?.heading ?? "",
        city: row?.city ?? "",
        description: row?.description ?? "",
        category: row?.category ?? "",
        author: row?.author ?? "",
        read_time_minutes: row?.read_time_minutes ?? 0,
        is_deleted: true,
        wedding_photos: [],
      })
    );
  };

  const columns = [
    {
      key: "id",
      title: "ID",
      dataIndex: "id",
      render: (row) => row?.id ?? row?.real_wedding_id ?? "—",
    },
    {
      key: "cover",
      title: "Cover",
      render: (row) => {
        const cover =
          row?.cover_photo ??
          row?.cover_image_url ??
          (Array.isArray(row?.wedding_photos)
            ? row.wedding_photos.find((p) => p?.type === "cover")?.image_url ??
              row.wedding_photos[0]?.image_url
            : null);
        if (!cover) return "—";
        return (
          <div className="admin-real-weddings-table-thumb">
            <img src={cover} alt="" />
          </div>
        );
      },
    },
    {
      key: "heading",
      title: "Heading",
      dataIndex: "heading",
      render: (row) => row?.heading ?? row?.title ?? "—",
    },
    {
      key: "city",
      title: "City",
      dataIndex: "city",
      render: (row) => row?.city ?? "—",
    },
    ...(isBlog
      ? [
          {
            key: "category",
            title: "Category",
            render: (row) => row?.category ?? "—",
          },
        ]
      : []),
    {
      key: "description",
      title: "Description",
      render: (row) => {
        const desc = row?.description ?? "";
        const plain = desc.replace(/<[^>]*>/g, "").trim().slice(0, 80);
        return plain ? `${plain}${plain.length >= 80 ? "…" : ""}` : "—";
      },
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => {
        const id = row?.id ?? row?.real_wedding_id;
        if (id == null) return "—";
        return (
          <>
            <button
              type="button"
              className="admin-btn admin-btn-sm admin-btn-outline"
              onClick={() => navigate(`${editPathPrefix}/${id}`)}
            >
              Update
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-sm admin-btn-outline"
              onClick={() => navigate(`${viewPathPrefix}/${id}`)}
              style={{ marginLeft: "0.5rem" }}
            >
              View
            </button>
            <button
              type="button"
              className="admin-btn admin-btn-sm admin-btn-outline"
              onClick={() => setDeleteTarget(row)}
              disabled={postLoading && deletingId === id}
              style={{ marginLeft: "0.5rem" }}
            >
              {postLoading && deletingId === id ? "Deleting..." : "Delete"}
            </button>
          </>
        );
      },
    },
  ];

  return (
    <div className="admin-real-weddings-list">
      <div className="admin-page-header-row">
        <h1 className="admin-page-title">{title}</h1>
        <button
          type="button"
          className="admin-btn admin-btn-primary"
          onClick={() => navigate(addPath)}
        >
          {addLabel}
        </button>
      </div>
      <CommonTable
        columns={columns}
        data={list}
        loading={getListLoading}
        emptyText={emptyText}
        showSearch
        searchPlaceholder={searchPlaceholder}
        searchKeys={["heading", "description", "id", "city", ...(isBlog ? ["category"] : [])]}
      />

      {deleteTarget && (
        <div className="admin-delete-modal-overlay" role="dialog" aria-modal="true">
          <div className="admin-delete-modal">
            <h3 className="admin-delete-modal-title">Delete item</h3>
            <p className="admin-delete-modal-text">
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.heading || "this item"}</strong>?
            </p>
            <div className="admin-delete-modal-actions">
              <button
                type="button"
                className="admin-btn admin-btn-sm admin-btn-outline"
                onClick={() => setDeleteTarget(null)}
                disabled={postLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-sm admin-btn-primary"
                onClick={handleDeleteConfirm}
                disabled={postLoading}
              >
                {postLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentListPage;
